/**
 * Utility Selector Wizard
 *
 * A context-agnostic wizard component for selecting utility providers based on
 * postal code. Renders inside any container (top-nav dropdown, modal, etc.) and
 * does not manage its own open/close — the parent container owns that.
 *
 * Features:
 * - Postal code validation (CA/US formats)
 * - Province detection from postal code
 * - Dynamic provider list from metaobjects
 * - Program info display (utility_popup_copy)
 *
 * Events emitted:
 * - utility-wizard:provider-selected — Provider selected
 * - utility-wizard:close-requested — User clicked done/close
 * - utility-wizard:cleared — User cleared their utility selection
 *
 * Dependencies:
 * - utility-partner-manager.js: Provider data and selection management
 * - utility-storage.js: localStorage management
 * - mysa-events.js: Shared event constants and query-param helpers
 */

/**
 * Import types from utility modules
 * @typedef {import('./utility-partner-manager').UtilityPartner} UtilityPartner
 * @typedef {import('./utility-partner-manager').EnrichedMatch} EnrichedMatch
 * @typedef {import('./utility-storage').UtilitySelectionData} UtilitySelectionData
 */

/**
 * @typedef {Object} WizardState
 * @property {string} postalCode - Current postal code
 * @property {string} country - Current country code
 * @property {UtilityPartner | null} selectedProvider - Currently selected provider
 * @property {boolean} noProvidersFound - Whether last search found no providers
 */

/**
 * @typedef {Object} WizardRefs
 * @property {HTMLElement} [utilityContent] - Main content container
 * @property {HTMLElement} [results] - Results container
 * @property {HTMLElement} [noResults] - No results message
 * @property {HTMLElement} [providersList] - Provider cards container
 * @property {HTMLTemplateElement} [providerTemplate] - Template for provider cards
 * @property {HTMLElement} [clearUtilityBtn] - Clear selection button
 * @property {HTMLButtonElement} [geolocationBtn] - Geolocation button
 * @property {HTMLSelectElement} [countrySelect] - Country selector
 * @property {HTMLElement} [matchesButtons] - Footer for matched providers
 * @property {HTMLElement} [noMatchesButtons] - Footer for no matches
 * @property {HTMLButtonElement} [findYourMysaButton] - Find Your Mysa CTA
 * @property {HTMLButtonElement} [continueShoppingButton] - Continue shopping CTA
 * @property {HTMLButtonElement} [matchesContinueButton] - Continue button in matches footer
 * @property {HTMLElement} [matchesOfferLinkWrap] - Program-page link wrapper in matches footer
 * @property {HTMLAnchorElement} [matchesOfferLink] - Program-page link in matches footer
 * @property {HTMLButtonElement} [backToProviders] - Back to providers button
 * @property {HTMLElement} [programInfo] - Program info container for utility_popup_copy
 * @property {HTMLElement} [programCopy] - Program copy text container
 */

import { Component } from "@theme/component";
import { getActiveCurrency, sendGaEvent } from "@theme/ga-events";
import {
  getUtilityPartnerManager,
  programCollectionPath,
  UtilityPartnerManager,
} from "@theme/utility-partner-manager";
import {
  getCountryFromPostalCode,
  getCountryFromProviderCode,
  UtilityStorage,
  show,
  hide,
} from "@theme/utility-storage";
import { createLogger, createReporter } from "@theme/logger";
import { MysaEvents, hasUtilityQueryOverride } from "@theme/mysa-events";
import { IPGeolocationService } from "@theme/ip-geolocation";
import { safeSubmit } from "@theme/mysa-utilities";
import { safeUrl } from "@theme/safe-url";
import { sanitizeSvg } from "@theme/sanitize-svg";

const log = createLogger("UtilitySelectorWizard");
const reportError = createReporter("UtilitySelectorWizard");

/** Card modifier classes keyed by resolved mysa_offer_mode (or DR fallback). */
const OFFER_MODE_CARD_MODIFIERS = {
  instant_checkout: "utility-wizard__provider-card--instant-checkout",
  marketplace_only: "utility-wizard__provider-card--marketplace-only",
  post_purchase: "utility-wizard__provider-card--post-purchase",
  demand_response: "utility-wizard__provider-card--demand-response",
};

/**
 * Utility Selector Wizard Component
 *
 * Handles the wizard flow for utility selection.
 * Rendered inside a parent container (top-nav dropdown, modal, etc.).
 *
 * @extends {Component<WizardRefs>}
 */
class UtilitySelectorWizard extends Component {
  /** @type {WizardRefs} */
  refs = {};

  /** @type {boolean} True after initialize() so postal results can render */
  #managerReady = false;

  /** @type {CustomEvent | null} Check result that arrived during initialize() */
  #pendingPostalCheck = null;

  /** True after a stash/pending Check has been rendered — remorph reconnects skip */
  #postalReplayConsumed = false;

  /** True while a provider selection save has not finished */
  #selectionInFlight = false;

  /** Partner key for the in-flight save (`code` + program handle) */
  #selectionKey = "";

  /** @type {{ provider: UtilityPartner, card: HTMLElement } | null} */
  #queuedSelection = null;

  constructor() {
    super();

    /** @type {import('./utility-partner-manager').UtilityPartnerManager | null} */
    this.utilityManager = null;

    /** @type {AbortController | null} */
    this.abortController = null;

    /** @type {WizardState} */
    this.state = {
      postalCode: "",
      country: "",
      selectedProvider: null,
      noProvidersFound: false,
    };

    /** @type {boolean} Flag to track if clearing is in progress (for shouldShowWidgetOnClose during animation) */
    this.isClearing = false;

    /** @type {number | null} Timeout ID for the clear animation */
    this.clearTimeout = null;
  }

  async connectedCallback() {
    super.connectedCallback();

    // Create fresh AbortController for this connection
    this.abortController = new AbortController();

    // Bind before initialize() so an auto-open Check that finishes while the
    // manager is still parsing doesn't drop PostalCodeCheckedEvent. Results
    // that arrive early are stashed and replayed once the manager is ready.
    this.country = this.dataset.country || "CA";
    this.postalPattern = new RegExp(this.dataset.postalPattern || ".*", "i");
    this.bindEvents();

    this.utilityManager = getUtilityPartnerManager();
    let replayedPostal = false;
    try {
      await this.utilityManager.initialize();
    } finally {
      // Consume even when initialize() fails so an auto-open Check is not
      // lost — handlePostalCodeChecked already degrades without providers.
      this.#managerReady = true;
      replayedPostal = this.#consumePendingPostalCheck();
    }

    // Check for Liquid pre-populated card (from cart attribute)
    const prePopulatedCard = this.detectPrePopulatedCard();
    const storedSelection = this.utilityManager?.getSelection();

    if (storedSelection?.partnerCode) {
      // Prefer storage-backed restore so programHandle / offerMode survive
      this.restoreExistingSelection();
    } else if (prePopulatedCard) {
      this.initializeFromPrePopulatedCard(prePopulatedCard);
    } else if (!replayedPostal) {
      // Reset content sections to hidden — Liquid may have pre-rendered
      // is-visible classes from cart attributes that no longer apply
      hide(this.refs.utilityContent);
      hide(this.refs.results);
      hide(this.refs.noResults);
      this.hideAllFooterButtons();

      // Pre-fill postal code if available
      this.prefillPostalCode();

      // Restore existing selection if available (will re-show content if valid)
      this.restoreExistingSelection();
    }

    log("Initialized");
  }

  /**
   * Detect if there's a pre-populated provider card from Liquid (cart attribute)
   * @returns {HTMLElement|null} The pre-populated card element, or null
   */
  detectPrePopulatedCard() {
    const selectedCard = this.refs.providersList?.querySelector(
      ".utility-wizard__provider-card.is-selected",
    );
    return /** @type {HTMLElement|null} */ (selectedCard) || null;
  }

  /**
   * Initialize state from a pre-populated card (rendered by Liquid from cart attributes)
   * @param {HTMLElement} card - The pre-populated card element
   */
  initializeFromPrePopulatedCard(card) {
    const radioInput = /** @type {HTMLInputElement | null} */ (
      card.querySelector('input[type="radio"]')
    );
    const providerCode = radioInput?.dataset.providerCode || "";
    const providerName =
      card.querySelector("[data-name]")?.textContent?.trim() || "";
    const programHandle = radioInput?.dataset.programHandle;
    const offerMode = radioInput?.dataset.offerMode;

    log("Found pre-populated card:", providerCode, providerName, {
      programHandle,
      offerMode,
    });

    const fullProvider = this.utilityManager?.getProviderByCode(providerCode);

    if (programHandle && fullProvider) {
      const family = this.utilityManager.resolveProgramFamily(
        fullProvider,
        programHandle,
      );

      if (family) {
        const programCard = this.buildProgramCard(
          {
            code: fullProvider.code,
            handle: fullProvider.handle || fullProvider.code,
            name: fullProvider.name,
            logoSvg: fullProvider.logoSvg,
            logoImg: fullProvider.logoImg,
          },
          fullProvider,
          { handle: family.handle, type: family.type },
        );

        this.state.selectedProvider = programCard;
        this.state.postalCode =
          UtilityStorage.getPostalCode()?.value ||
          this.utilityManager?.getSelection()?.postalCode ||
          "";
        this.displaySelectedProvider(programCard);
        return;
      }
    }

    this.state.selectedProvider =
      fullProvider ||
      /** @type {UtilityPartner} */ ({
        code: providerCode,
        name: providerName,
        handle: providerCode,
        region: "",
      });

    if (programHandle) {
      this.state.selectedProvider.programHandle = programHandle;
    }
    if (offerMode) {
      this.state.selectedProvider.mysaOfferMode = offerMode;
    }

    card.addEventListener("click", () => {
      if (this.state.selectedProvider) {
        this.handleProviderSelection(this.state.selectedProvider, card);
      }
    });

    show(this.refs.clearUtilityBtn);
    this.hideAllFooterButtons();

    if (fullProvider) {
      this.syncSelectedCta(this.state.selectedProvider);
      this.displayProgramInfo(this.state.selectedProvider);
    }
    show(this.refs.matchesButtons);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abortController?.abort();
    // Cancel any in-progress clear animation
    if (this.clearTimeout !== null) {
      clearTimeout(this.clearTimeout);
      this.clearTimeout = null;
      this.isClearing = false;
    }
  }

  /**
   * Handle container close — show widget if user progressed but didn't complete.
   * Called when the parent container (dropdown or modal) closes.
   *
   * Note: Dismissal for 1 day is set by the WIDGET close, not the container close.
   * This allows users to re-access the wizard via the widget.
   */
  async handleContainerClose() {
    this.#clearOpenedTracked();

    const shouldShowWidget = await this.shouldShowWidgetOnClose();

    if (shouldShowWidget) {
      const selection = UtilityStorage.getUtility();
      document.dispatchEvent(
        new CustomEvent("utility-wizard:show-widget", {
          bubbles: true,
          detail: {
            provider: selection,
            reason: shouldShowWidget,
            wizard: this,
          },
        }),
      );
      log(`Container closed, showing widget: ${shouldShowWidget}`);
    } else {
      const days = this.state.noProvidersFound ? 7 : 1;
      UtilityStorage.setDismissed(days);
      log(`Container closed, dismissed for ${days} day(s)`);
    }
  }

  /**
   * Determine if widget should show when container closes
   * @returns {Promise<string|false>} Reason to show widget, or false
   */
  async shouldShowWidgetOnClose() {
    // If user has selected a utility
    const selection = UtilityStorage.getUtility();
    log("shouldShowWidgetOnClose - selection:", selection);

    if (selection) {
      log("shouldShowWidgetOnClose -> false (selection complete)");
      return false;
    }

    // If no providers were found, don't show the widget
    if (this.state.noProvidersFound) {
      log("shouldShowWidgetOnClose -> false (no providers found)");
      return false;
    }

    // If utilities section was shown (user entered postal code and saw providers)
    // Check if section exists and is not hidden (or clearing in progress)
    const utilityContent = this.refs.utilityContent;
    const hasHiddenAttr = utilityContent?.hasAttribute("hidden");
    log(
      "shouldShowWidgetOnClose - utilityContent:",
      utilityContent,
      "hasHidden:",
      hasHiddenAttr,
      "isClearing:",
      this.isClearing,
    );

    // If clearing is in progress (animating out), treat as hidden
    if (this.isClearing) {
      log("shouldShowWidgetOnClose -> false (clearing in progress)");
      return false;
    }

    if (utilityContent && !hasHiddenAttr) {
      log("shouldShowWidgetOnClose -> utilities-shown");
      return "utilities-shown";
    }

    log("shouldShowWidgetOnClose -> false (no utilities shown)");
    return false;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (!this.abortController) return;
    const signal = this.abortController.signal;

    // "Not right now" dismiss button (inside postal-code-checker, dispatches utility-wizard:dismiss-requested)
    this.addEventListener(
      "utility-wizard:dismiss-requested",
      () => {
        const days = this.state.noProvidersFound ? 7 : 1;
        UtilityStorage.setDismissed(days);
        this.emitCloseRequested();
      },
      { signal },
    );

    // Country selector - native select change event
    this.refs.countrySelect?.addEventListener(
      "change",
      (e) => {
        const target = /** @type {HTMLSelectElement} */ (e.target);
        this.selectCountry(target.value);
      },
      { signal },
    );

    // Listen for form submit on postal-code-checker to hide noProviders message immediately
    const postalChecker = this.querySelector("postal-code-checker");
    const postalForm = postalChecker?.querySelector("form");
    postalForm?.addEventListener(
      "submit",
      () => this.handlePostalCodeSearchStart(),
      { signal },
    );

    // Listen for postal-code-checked event from embedded postal-code-checker block
    this.addEventListener(
      MysaEvents.postalCodeChecked,
      (e) => this.handlePostalCodeChecked(/** @type {CustomEvent} */ (e)),
      { signal },
    );

    // Clear utility button
    this.refs.clearUtilityBtn?.addEventListener(
      "click",
      () => this.clearUtilitySelection(),
      { signal },
    );

    // No matches footer buttons
    this.refs.findYourMysaButton?.addEventListener(
      "click",
      () => this.handleFindYourMysa(),
      { signal },
    );
    this.refs.continueShoppingButton?.addEventListener(
      "click",
      () => {
        UtilityStorage.setDismissed(7);
        this.emitCloseRequested();
      },
      { signal },
    );

    // Matches footer buttons
    this.refs.matchesContinueButton?.addEventListener(
      "click",
      () => this.emitCloseRequested(),
      { signal },
    );

    // Navigation buttons
    this.refs.backToProviders?.addEventListener(
      "click",
      () => this.showStep("providers"),
      { signal },
    );

    // Listen for container open/close (details toggle or dialog open attr).
    // Also sync if already open — auto-open (?utility=, ?region=, popup_delay)
    // can set details.open before this element upgrades.
    const parentDetails = this.closest("details");
    if (parentDetails) {
      parentDetails.addEventListener(
        "toggle",
        () => {
          if (parentDetails.open) {
            this.#trackOpened();
          } else {
            this.handleContainerClose();
          }
        },
        { signal },
      );
      if (parentDetails.open) {
        this.#trackOpened();
      }
    } else {
      const parentDialog = this.closest("dialog");
      if (parentDialog) {
        // Dialog `toggle` is missing on Safari <26 and Chromium/Firefox before
        // ~132/133. Observe the `open` attribute so modal opens still track.
        const openObserver = new MutationObserver(() => {
          if (parentDialog.open) {
            this.#trackOpened();
          }
        });
        openObserver.observe(parentDialog, {
          attributes: true,
          attributeFilter: ["open"],
        });
        signal.addEventListener("abort", () => openObserver.disconnect(), {
          once: true,
        });

        parentDialog.addEventListener(
          "close",
          () => this.handleContainerClose(),
          { signal },
        );
        if (parentDialog.open) {
          this.#trackOpened();
        }
      }
    }

    // Listen for utility changes from other components (e.g., top-nav)
    document.addEventListener(
      MysaEvents.utilityPartnerChanged,
      (e) => this.handleExternalUtilityChange(/** @type {CustomEvent} */ (e)),
      { signal },
    );

    // Listen for utility cleared from other components
    document.addEventListener(
      MysaEvents.utilityPartnerCleared,
      () => this.handleExternalUtilityCleared(),
      { signal },
    );

    // Handle browser back/forward navigation (bfcache restoration)
    window.addEventListener("pageshow", (e) => this.handlePageShow(e), {
      signal,
    });

    // Restart from parent UI (e.g. top-nav bottom sheet) — full reset
    this.addEventListener(
      "utility-wizard:restart-requested",
      () => {
        void this.clearUtilitySelection();
      },
      { signal },
    );

    // Restart from mysa-modal (product finder, Typeform, etc.)
    const hostDialog = this.closest("dialog");
    hostDialog?.addEventListener(
      "modal:restart",
      () => {
        void this.clearUtilitySelection();
      },
      { signal },
    );
  }

  /**
   * Handle pageshow event for bfcache restoration
   * Ensures UI state is properly restored when navigating back
   * @param {PageTransitionEvent} event
   */
  handlePageShow(event) {
    // Only handle bfcache restoration (persisted = true)
    if (!event.persisted) return;

    log("Page restored from bfcache, restoring UI state");

    // Re-sync UI with current state
    this.restoreUIStateFromSelection();
  }

  /**
   * Restore UI state based on current selection
   * Called after bfcache restoration to ensure footer visibility matches state
   */
  restoreUIStateFromSelection() {
    const selection = UtilityStorage.getUtility();

    // If we have a selection, ensure the correct footer is visible
    if (selection) {
      this.hideAllFooterButtons();
      show(this.refs.matchesButtons);
      if (this.state.selectedProvider) {
        this.syncSelectedCta(this.state.selectedProvider);
      }

      // Ensure content wrapper and results are visible
      show(this.refs.utilityContent);
      show(this.refs.results);
    } else {
      // No selection - check if we had cached matches
      const storedPostal = UtilityStorage.getPostalCode();
      if (storedPostal?.value) {
        const cachedMatches = UtilityStorage.getLastMatches(storedPostal.value);
        if (cachedMatches && cachedMatches.length > 0) {
          // Had providers but no selection - content should be visible
          show(this.refs.utilityContent);
          show(this.refs.results);
        } else if (cachedMatches && cachedMatches.length === 0) {
          // No providers found - show no results
          show(this.refs.utilityContent);
          show(this.refs.noResults);
          show(this.refs.noMatchesButtons);
        }
      }
    }
  }

  /**
   * Handle utility selection made from another component (e.g., top-nav)
   * Syncs the wizard state with the external selection
   * @param {CustomEvent} event
   */
  handleExternalUtilityChange(event) {
    const { partnerCode, programHandle, offerMode } = event.detail || {};
    if (!partnerCode) return;

    const current = this.state.selectedProvider;
    const matchesProvider =
      current?.code === partnerCode || current?.handle === partnerCode;
    const matchesCurrentSelection =
      matchesProvider &&
      (current?.programHandle ?? "") === (programHandle ?? "") &&
      (current?.mysaOfferMode ?? "") === (offerMode ?? "");

    // Skip only when provider and program selection are unchanged
    if (matchesCurrentSelection) {
      log("External change matches current selection, skipping");
      return;
    }

    log("External utility change detected:", partnerCode, programHandle);

    // Update internal state
    const provider = this.utilityManager?.getProviderByCode(partnerCode);
    if (provider) {
      // Restore postal code from storage
      const storedPostal = UtilityStorage.getPostalCode();
      if (storedPostal?.value) {
        this.state.postalCode = storedPostal.value;

        // Pre-fill postal input
        const postalChecker = this.querySelector("postal-code-checker");
        const postalInput = /** @type {HTMLInputElement|null} */ (
          postalChecker?.querySelector('[ref="postalInput"]')
        );
        if (postalInput) {
          postalInput.value = storedPostal.value;
        }
      }

      if (programHandle) {
        const family = this.utilityManager.resolveProgramFamily(
          provider,
          programHandle,
        );

        if (family) {
          const programCard = this.buildProgramCard(
            {
              code: provider.code,
              handle: provider.handle || provider.code,
              name: provider.name,
              logoSvg: provider.logoSvg,
              logoImg: provider.logoImg,
            },
            provider,
            { handle: family.handle, type: family.type },
          );

          if (offerMode) {
            programCard.mysaOfferMode = offerMode;
          }

          this.state.selectedProvider = programCard;
          this.displaySelectedProvider(programCard);
          // Locale switch is owned by saveProviderSelection on the selecting
          // instance — do not re-submit localization from every synced wizard.
          return;
        }
      }

      this.state.selectedProvider = provider;
      this.displaySelectedProvider(provider);
    }
  }

  /**
   * Handle utility cleared from another component
   * Resets the wizard UI to its initial state
   */
  handleExternalUtilityCleared() {
    // Skip if this was triggered by our own clearUtilitySelection() call
    // to avoid defeating the synchronized fade-out animation
    if (this.isClearing) return;

    log("External utility cleared, resetting wizard state");

    this.state.selectedProvider = null;

    // Reset shared UI elements
    this.resetSharedUIElements();

    // Hide program info (utility_popup_copy)
    hide(this.refs.programInfo);

    // Uncheck all provider cards
    const allCards = this.refs.providersList?.querySelectorAll(
      ".utility-wizard__provider-card",
    );
    allCards?.forEach((card) => {
      const radio = /** @type {HTMLInputElement|null} */ (
        card.querySelector(".utility-radio")
      );
      if (radio) radio.checked = false;
    });
  }

  /**
   * Reset UI elements shared between multiple reset methods
   * Used by handleExternalUtilityCleared and resetUtilitySectionUI
   */
  resetSharedUIElements() {
    this.#setHasSelection(false);
    hide(this.refs.clearUtilityBtn);
    this.hideAllFooterButtons();
  }

  /**
   * Mark whether a program card is selected. The host class lets child blocks
   * (postal-code-checker) react without the wizard reaching into their markup.
   * @param {boolean} hasSelection
   */
  #setHasSelection(hasSelection) {
    this.classList.toggle("has-selection", hasSelection);
    this.refs.providersList?.classList.toggle("has-selection", hasSelection);
  }

  /**
   * Pre-fill postal code from storage if available
   */
  prefillPostalCode() {
    const stored = UtilityStorage.getPostalCode();
    if (stored?.value) {
      // Find the postal-code-checker and set its input value
      const postalChecker = this.querySelector("postal-code-checker");
      const postalInput = /** @type {HTMLInputElement|null} */ (
        postalChecker?.querySelector('[ref="postalInput"]')
      );
      if (postalInput) {
        postalInput.value = stored.value;
      }
    }
  }

  /**
   * Restore existing utility selection on component mount
   * Shows the selected utility with Edit/Clear buttons if user has previously selected one
   * Also restores cached matches if no selection but cached data exists
   */
  restoreExistingSelection() {
    if (!this.utilityManager) return;

    const selection = this.utilityManager.getSelection();
    if (!selection?.partnerCode) {
      // No selection, but try to restore cached matches
      const storedPostal = UtilityStorage.getPostalCode();
      if (storedPostal?.value) {
        const restored = this.restoreCachedMatches(storedPostal.value);
        if (restored) {
          log("Restored cached matches without selection");
        }
      }
      return;
    }

    // Get full provider data using utility-partner-manager (single source of truth)
    let provider = this.utilityManager.getProviderByCode(selection.partnerCode);

    // Fallback: try by name
    if (!provider) {
      provider = this.utilityManager.getProviderByName(selection.partnerName);
    }

    if (!provider) {
      log("Could not find provider data for selection:", selection);
      return;
    }

    if (selection.programHandle) {
      const family = this.utilityManager.resolveProgramFamily(
        provider,
        selection.programHandle,
      );

      if (family) {
        const programCard = this.buildProgramCard(
          {
            code: provider.code,
            handle: provider.handle || provider.code,
            name: provider.name,
            logoSvg: provider.logoSvg,
            logoImg: provider.logoImg,
          },
          provider,
          { handle: family.handle, type: family.type },
        );

        if (selection.offerMode) {
          programCard.mysaOfferMode = selection.offerMode;
        }

        this.state.selectedProvider = programCard;
        this.state.postalCode = selection.postalCode || "";
        this.displaySelectedProvider(programCard);
        return;
      }
    }

    log("Restoring existing selection:", provider.name);

    // Update state
    this.state.selectedProvider = provider;
    this.state.postalCode = selection.postalCode || "";

    // Display the selected provider
    this.displaySelectedProvider(provider);
  }

  /**
   * Pick the program card that matches persisted or in-memory program selection.
   * Avoids selecting the first same-provider card when multiple programs exist.
   * @param {UtilityPartner[]} cards
   * @param {UtilitySelectionData|null} storedUtility
   * @param {UtilityPartner} provider
   * @returns {UtilityPartner|null}
   */
  #resolveSelectedProgramCard(cards, storedUtility, provider) {
    const programHandle =
      storedUtility?.programHandle || provider.programHandle || "";

    if (programHandle) {
      return cards.find((card) => card.programHandle === programHandle) || null;
    }

    const sameCodeCards = cards.filter((card) => card.code === provider.code);
    if (sameCodeCards.length === 1) {
      return sameCodeCards[0];
    }

    return null;
  }

  /**
   * Display a selected provider with its card and appropriate footer/step.
   * Shared logic used by restoreExistingSelection, handleExternalUtilityChange, etc.
   * @param {UtilityPartner} provider - The provider to display as selected
   */
  displaySelectedProvider(provider) {
    const storedPostal = UtilityStorage.getPostalCode();
    const cachedMatches = storedPostal?.value
      ? UtilityStorage.getLastMatches(storedPostal.value)
      : null;

    if (cachedMatches?.length) {
      const cards = this.getProgramCardsFromEnrichedMatches(
        /** @type {EnrichedMatch[]} */ (cachedMatches),
      );
      const storedUtility = UtilityStorage.getUtility();
      const selectedCard = this.#resolveSelectedProgramCard(
        cards,
        storedUtility,
        provider,
      );

      if (selectedCard) {
        // Catalog rebuild can omit offer mode; prefer persisted / in-memory mode.
        const persistedMode =
          provider.mysaOfferMode || storedUtility?.offerMode || "";
        if (persistedMode) {
          selectedCard.mysaOfferMode = persistedMode;
        }
        this.renderProgramCardsWithSelection(cards, selectedCard);
        return;
      }
    }

    this.renderProviders([provider]);
    const cards = this.refs.providersList?.querySelectorAll(
      ".utility-wizard__provider-card",
    );
    // Multi-program: do not default to card 0 (wrong family vs storage/cart).
    if (cards?.length === 1) {
      const onlyCard = cards[0];
      onlyCard.classList.add("is-selected");
      const radio = /** @type {HTMLInputElement|null} */ (
        onlyCard.querySelector('input[type="radio"]')
      );
      if (radio) radio.checked = true;
      this.#setHasSelection(true);
    }

    show(this.refs.clearUtilityBtn);
    this.hideAllFooterButtons();

    show(this.refs.matchesButtons);
    this.syncSelectedCta(provider);
    this.displayProgramInfo(provider);

    show(this.refs.results);
    show(this.refs.utilityContent);
  }

  /**
   * Restore cached matches from storage (no network request needed)
   * Called when user opens wizard and has a cached postal code lookup
   * @param {string} postalCode - Postal code to check for cached matches
   * @returns {boolean} True if cached matches were restored
   */
  restoreCachedMatches(postalCode) {
    const cachedMatches = /** @type {EnrichedMatch[]|null} */ (
      UtilityStorage.getLastMatches(postalCode)
    );
    if (!cachedMatches) return false;

    // Guard: if provider data didn't load and we have non-empty matches,
    // cached enrichment is unreliable — bail before touching state/DOM.
    const loadedProviders = this.utilityManager?.getAllProviders() || [];
    if (cachedMatches.length > 0 && loadedProviders.length === 0) {
      log("Provider data not loaded — skipping cached match restore");
      return false;
    }

    // Safe to commit — update state and reset selection UI
    this.state.postalCode = postalCode;
    this.state.country = getCountryFromPostalCode(postalCode);
    this.state.selectedProvider = null;
    this.#setHasSelection(false);
    hide(this.refs.clearUtilityBtn);
    hide(this.refs.programInfo);
    this.hideAllFooterButtons();

    if (cachedMatches.length === 0) {
      this.state.noProvidersFound = true;
      if (this.refs.providersList) this.refs.providersList.innerHTML = "";
      hide(this.refs.results);
      show(this.refs.noResults);
      show(this.refs.noMatchesButtons);
      show(this.refs.utilityContent);
      return true;
    }

    log("Restoring cached matches for:", postalCode);

    this.state.noProvidersFound = false;

    const providers = this.getProgramCardsFromEnrichedMatches(cachedMatches);
    this.renderProviders(providers);
    show(this.refs.results);
    hide(this.refs.noResults);
    show(this.refs.utilityContent);

    return true;
  }

  /**
   * Emit custom event for parent components to listen to
   * @param {string} eventName
   * @param {Object} detail
   */
  emit(eventName, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        composed: true,
        detail: { ...detail, wizard: this },
      }),
    );
  }

  /**
   * Emit close-requested event — the parent container handles closing
   */
  emitCloseRequested() {
    this.emit("utility-wizard:close-requested", {
      state: this.state,
    });
  }

  /**
   * Handle the location button.
   * A confirmed rebate postal is submitted. Otherwise the field is filled with
   * the geo prefix when that hint is for this market, and left focused so the
   * visitor can finish the code. Nothing is written to utility storage until
   * they submit.
   */
  handleGeolocation() {
    const postalChecker = this.querySelector("postal-code-checker");
    const postalInput = /** @type {HTMLInputElement|null} */ (
      postalChecker?.querySelector('[ref="postalInput"]')
    );
    const form = /** @type {HTMLFormElement|null} */ (
      postalChecker?.querySelector("form")
    );
    const postalError = postalChecker?.querySelector('[ref="postalError"]');
    const postalCode = UtilityStorage.getPostalCode()?.value || null;

    if (postalCode) {
      if (postalInput) {
        postalInput.value = postalCode.toUpperCase().replace(/\s+/g, "");
      }
      if (form) {
        safeSubmit(form);
      }
      return;
    }

    const location = IPGeolocationService.getLocationData();
    const sameCountry =
      location.country.toUpperCase() === this.country.toUpperCase();

    if (location.prefix && sameCountry && postalInput) {
      postalInput.value = location.prefix;
      postalInput.focus();
      return;
    }

    postalInput?.focus();
    if (postalError) {
      postalError.textContent =
        "Could not detect location. Please enter manually.";
      show(postalError);
      setTimeout(() => {
        hide(postalError);
      }, 3000);
    }
  }

  /**
   * Select a locale (country or country|language combination)
   * Accepts "CA|en" (from the select) or plain "CA" (from geolocation).
   * Submits the localization form when country or language differs from current locale.
   * @param {string} localeValue
   */
  selectCountry(localeValue) {
    const select = this.refs.countrySelect;
    if (!select) return;

    let countryCode;
    let languageCode = this.dataset.language;
    /** @type {HTMLOptionElement | null} */
    let selectedOption;

    if (localeValue.includes("|")) {
      [countryCode, languageCode] = localeValue.split("|");
      selectedOption = /** @type {HTMLOptionElement | null} */ (
        select.querySelector(`option[value="${localeValue}"]`)
      );
    } else {
      countryCode = localeValue;
      const currentLang = this.dataset.language;
      selectedOption =
        /** @type {HTMLOptionElement | null} */ (
          select.querySelector(`option[value="${countryCode}|${currentLang}"]`)
        ) ??
        /** @type {HTMLOptionElement | null} */ (
          select.querySelector(`option[data-country-code="${countryCode}"]`)
        );
      if (selectedOption) {
        select.value = selectedOption.value;
        [, languageCode] = selectedOption.value.split("|");
      }
    }

    if (!countryCode) return;

    const localizationForm = /** @type {HTMLFormElement | null} */ (
      this.querySelector("#utility-wizard-localization")
    );
    const countryInput = /** @type {HTMLInputElement | null} */ (
      localizationForm?.querySelector('input[name="country_code"]')
    );
    const languageInput = /** @type {HTMLInputElement | null} */ (
      localizationForm?.querySelector('input[name="language_code"]')
    );

    if (countryInput) countryInput.value = countryCode;
    if (languageInput && languageCode) languageInput.value = languageCode;

    const currentCountry = this.dataset.country;
    const currentLanguage = this.dataset.language;
    const localeChanged =
      countryCode !== currentCountry || languageCode !== currentLanguage;

    if (localeChanged) {
      if (countryCode !== currentCountry) {
        const currentUtility = UtilityStorage.getUtility();
        if (currentUtility?.code) {
          const utilityCountry =
            getCountryFromProviderCode(currentUtility.code) ||
            UtilityStorage.getPostalCode()?.country ||
            "";
          if (
            utilityCountry &&
            utilityCountry.toUpperCase() !== countryCode.toUpperCase()
          ) {
            log(
              `Clearing all utility data - country mismatch (${utilityCountry} vs ${countryCode})`,
            );
            UtilityStorage.clear();
          }
        }
      }

      if (localizationForm) {
        log(
          `Switching locale from ${currentCountry}|${currentLanguage} to ${countryCode}|${languageCode}`,
        );
        localizationForm.submit();
        return;
      }
    }

    this.country = countryCode;
    this.state.country = countryCode;

    const postalChecker = this.querySelector("postal-code-checker");
    const postalInput = /** @type {HTMLInputElement | null} */ (
      postalChecker?.querySelector('[ref="postalInput"]')
    );

    const pattern = selectedOption?.dataset.postalPattern;
    const placeholder = selectedOption?.dataset.postalPlaceholder;

    if (pattern) {
      this.postalPattern = new RegExp(pattern, "i");
    }
    if (placeholder && postalInput) {
      postalInput.setAttribute("placeholder", placeholder);
    }

    if (postalInput) postalInput.value = "";
    this.hideUtilitySection();
  }

  /**
   * Handle Find Your Mysa button click — emits close-requested so the
   * parent container closes, then opens the typeform find-products modal.
   */
  handleFindYourMysa() {
    this.emitCloseRequested();

    // Delay modal open to let the dropdown's closing animation complete.
    // The dropdown uses a 0.4s CSS transition (--wizard-animation-duration).
    const closeAnimationDuration = 400;

    setTimeout(() => {
      const modal = /** @type {{ openModal?: () => void } | null} */ (
        document.querySelector(
          'mysa-modal[data-url-parameter="typeform-find-products"]',
        )
      );
      modal?.openModal?.();
    }, closeAnimationDuration);
  }

  /**
   * Hide utility content and reset state
   */
  hideUtilitySection() {
    hide(this.refs.utilityContent);
    hide(this.refs.results);
    hide(this.refs.noResults);
    this.#setHasSelection(false);
    hide(this.refs.clearUtilityBtn);
    this.state.selectedProvider = null;
  }

  /**
   * Clear utility selection - reset to initial state (postal code input only)
   */
  async clearUtilitySelection() {
    log("Clearing utility selection - resetting to initial state");

    // Mark clearing in progress FIRST to prevent self-triggered event handlers
    // from defeating the synchronized fade-out animation
    this.isClearing = true;

    // Restart paths also call this for a full UI reset; only count abandonment
    // when a provider was actually selected.
    const hadSelection = Boolean(this.state.selectedProvider?.code);

    UtilityStorage.clear();
    if (this.utilityManager) {
      await this.utilityManager.clearSelection();
    }

    if (hadSelection) {
      this.#trackCleared();
    }

    // Reset state to initial
    this.state.selectedProvider = null;
    this.state.postalCode = "";
    this.state.noProvidersFound = false;

    // Phase 1: Fade out content
    this.refs.utilityContent?.classList.add("is-fading-out");

    // Phase 2: After fade, collapse instantly (no transitions)
    const fadeDuration = 200; // matches CSS transition
    this.clearTimeout = setTimeout(() => {
      this.clearTimeout = null;
      // Add is-collapsing to disable all transitions during collapse
      this.classList.add("is-collapsing");
      this.refs.utilityContent?.classList.remove("is-fading-out");

      // Hide everything instantly
      this.resetUtilitySectionUI();
      hide(this.refs.programInfo);

      // Reset clearing flag immediately so it can't get stuck true when the tab
      // is hidden (requestAnimationFrame pauses in background tabs).
      this.isClearing = false;

      // Clean up classes after a frame for visual correctness
      requestAnimationFrame(() => {
        this.classList.remove("is-collapsing");
      });
    }, fadeDuration);

    this.emit("utility-wizard:cleared");
  }

  /**
   * Reset utility section UI after animation or immediately
   */
  resetUtilitySectionUI() {
    // Hide the entire utility content wrapper
    hide(this.refs.utilityContent);

    // Clear providers list content
    if (this.refs.providersList) {
      this.refs.providersList.innerHTML = "";
    }

    // Reset shared UI elements
    this.resetSharedUIElements();

    // Hide no providers message
    hide(this.refs.noResults);

    // Clear postal code input
    const postalInput = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="postal_code"]')
    );
    if (postalInput) postalInput.value = "";
  }

  /**
   * Show a specific step
   * @param {string} stepName
   */
  showStep(stepName) {
    const steps = this.querySelectorAll(".utility-wizard__step");
    steps.forEach((step) => {
      const htmlStep = /** @type {HTMLElement} */ (step);
      if (htmlStep.dataset.step === stepName) {
        show(htmlStep);
      } else {
        hide(htmlStep);
      }
    });
  }

  /**
   * Handle postal code search start - clear UI immediately
   * Called when the postal code form is submitted
   * Note: postal-code-checker manages its own loading spinner, so we don't show ours
   */
  handlePostalCodeSearchStart() {
    log("Postal code search started, clearing UI");
    this.#pendingPostalCheck = null;
    this.#takeCheckerStash();

    // Hide all content immediately — including program info from previous provider
    hide(this.refs.noResults);
    hide(this.refs.noMatchesButtons);
    hide(this.refs.results);
    hide(this.refs.programInfo);

    // Clear provider list HTML
    if (this.refs.providersList) {
      this.refs.providersList.innerHTML = "";
    }

    // Reset noProvidersFound state - will be set after results come back
    this.state.noProvidersFound = false;

    // Reset previous selection when starting a new search
    this.state.selectedProvider = null;
    this.#setHasSelection(false);
    hide(this.refs.clearUtilityBtn);
    this.hideAllFooterButtons();
  }

  /**
   * Atomically take the checker's one-shot stash (null if already consumed
   * or the checker is not a descendant).
   * @returns {object | null}
   */
  #takeCheckerStash() {
    const checker = this.querySelector("postal-code-checker");
    if (typeof checker?.takeLastCheckedDetail !== "function") return null;
    return checker.takeLastCheckedDetail();
  }

  /**
   * Apply a Check result that arrived before initialize() finished, or that
   * the eager postal-code-checker already dispatched before this element
   * upgraded (auto-open campaign / ?region= / popup_delay). One-shot: a
   * remorph reconnect must not re-render or re-track the same lookup.
   * @returns {boolean} Whether a pending result was rendered
   */
  #consumePendingPostalCheck() {
    if (!this.#managerReady || this.#postalReplayConsumed) return false;

    if (this.#pendingPostalCheck) {
      const pending = this.#pendingPostalCheck;
      this.#pendingPostalCheck = null;
      this.#takeCheckerStash();
      this.#postalReplayConsumed = true;
      this.handlePostalCodeChecked(pending);
      return true;
    }

    const detail = this.#takeCheckerStash();
    if (!detail) return false;

    this.#postalReplayConsumed = true;
    this.handlePostalCodeChecked(/** @type {CustomEvent} */ ({ detail }));
    return true;
  }

  /**
   * Handle postal-code-checked event from postal-code-checker block
   * @param {CustomEvent} event
   */
  handlePostalCodeChecked(event) {
    if (!this.#managerReady) {
      this.#pendingPostalCheck = event;
      return;
    }

    // Discard any unread stash so a remorph reconnect cannot replay this.
    this.#takeCheckerStash();
    this.#postalReplayConsumed = true;

    const { postalCode, success, matches } = event.detail;

    log("Received postal-code-checked:", {
      postalCode,
      success,
      matchCount: matches?.length,
    });

    // Update state
    this.state.postalCode = postalCode;
    this.state.country = getCountryFromPostalCode(postalCode);

    UtilityStorage.setPostalCode(
      postalCode,
      "user-confirmed",
      this.state.country,
    );

    // Get providers from enriched matches (already enriched by postal-code-checker)
    /** @type {UtilityPartner[]} */
    let providers = [];
    if (success && matches?.length > 0) {
      // Guard: if provider data didn't load, enrichment produces empty cards
      const loadedProviders = this.utilityManager?.getAllProviders() || [];
      if (loadedProviders.length === 0) {
        log("Provider data not loaded — cannot display matches");
        providers = [];
      } else {
        providers = this.getProgramCardsFromEnrichedMatches(matches);
      }
    }

    // Track if no providers were found (for widget display logic)
    this.state.noProvidersFound = providers.length === 0;

    // Reset selection state to show fresh provider list
    this.state.selectedProvider = null;
    this.#setHasSelection(false);
    hide(this.refs.clearUtilityBtn);

    // Hide all footers first
    this.hideAllFooterButtons();

    // Render providers or show no-providers message
    if (providers.length === 0) {
      // No providers - show no results state
      hide(this.refs.results);
      show(this.refs.noResults);
      show(this.refs.noMatchesButtons);
      if (this.refs.providersList) this.refs.providersList.innerHTML = "";
    } else {
      // Providers found - show results
      this.renderProviders(providers);
      show(this.refs.results);
      hide(this.refs.noResults);
    }

    // Show content wrapper
    show(this.refs.utilityContent);

    // Only count resolved lookups — network/HTTP failures dispatch success:false
    // and must not inflate the postal funnel as empty-result submits.
    if (success) {
      this.#trackPostalSubmitted(providers);
    }
  }

  /**
   * Hide all utility section footer buttons
   */
  hideAllFooterButtons() {
    hide(this.refs.noMatchesButtons);
    hide(this.refs.matchesButtons);
  }

  /**
   * Convert enriched matches to one card per matched program.
   * @param {EnrichedMatch[]} matches - Enriched matches from postal code lookup
   * @returns {UtilityPartner[]}
   */
  getProgramCardsFromEnrichedMatches(matches) {
    /** @type {UtilityPartner[]} */
    const cards = [];

    for (const match of matches) {
      const fullProvider = this.utilityManager?.getProviderByCode(match.code);
      const apiPrograms = this.getApiProgramsFromMatch(match);

      if (apiPrograms.length === 0) {
        cards.push(this.buildUtilityCard(match, fullProvider));
        continue;
      }

      for (const apiProgram of apiPrograms) {
        cards.push(this.buildProgramCard(match, fullProvider, apiProgram));
      }
    }

    return cards;
  }

  /**
   * Extract program entries from a match (API array or legacy nested object).
   * @param {EnrichedMatch} match
   * @returns {Array<{ handle: string, type?: string, validationType?: string, validationCity?: string | null }>}
   */
  getApiProgramsFromMatch(match) {
    if (Array.isArray(match.programs)) {
      return match.programs.filter((program) => program?.handle);
    }

    if (!match.programs || typeof match.programs !== "object") {
      return [];
    }

    /** @type {Array<{ handle: string, type?: string }>} */
    const programs = [];

    if (match.programs.rebate?.program_handle) {
      programs.push({
        handle: match.programs.rebate.program_handle,
        type: "rebate",
      });
    }

    if (match.programs.demandResponse?.handle) {
      programs.push({
        handle: match.programs.demandResponse.handle,
        type: "demand-response",
      });
    }

    return programs;
  }

  /**
   * Build a fallback card when no program list is available on the match.
   * @param {EnrichedMatch} match
   * @param {UtilityPartner | null | undefined} fullProvider
   * @returns {UtilityPartner}
   */
  buildUtilityCard(match, fullProvider) {
    // Prefer region stamped by enrichMatches; fall back to catalog province
    const region =
      match.region ||
      UtilityPartnerManager.regionFromProvider(fullProvider) ||
      "";

    return /** @type {UtilityPartner} */ ({
      code: match.code,
      handle: match.handle || fullProvider?.handle || "",
      name: match.name,
      providerName: match.name,
      region,
      logoSvg: match.logoSvg || fullProvider?.logoSvg || "",
      logoImg: match.logoImg || fullProvider?.logoImg || "",
      popupCopy: match.popupCopy || fullProvider?.popupCopy || "",
      widgetDescription:
        match.widgetDescription || fullProvider?.widgetDescription || "",
      active: true,
      hasSso: fullProvider?.hasSso === true,
      programs: fullProvider?.programs || {},
      discountRules: fullProvider?.discountRules,
      discountPrograms: fullProvider?.discountPrograms,
    });
  }

  /**
   * Build a selectable card for a single matched program.
   * @param {EnrichedMatch} match
   * @param {UtilityPartner | null | undefined} fullProvider
   * @param {{ handle: string, type?: string }} apiProgram
   * @returns {UtilityPartner}
   */
  buildProgramCard(match, fullProvider, apiProgram) {
    const family = this.utilityManager?.resolveProgramFamily(
      fullProvider,
      apiProgram.handle,
      apiProgram.type,
    );
    const programType = String(
      apiProgram.type || family?.type || "",
    ).toLowerCase();
    const programName =
      family?.name || this.formatProgramHandle(apiProgram.handle);
    const providerName = match.name;
    const primaryIncentive = family?.primaryIncentive || null;
    const mysaOfferMode = family?.mysaOfferMode || "";
    const programKey = family?.handle || apiProgram.handle;
    const programDiscounts = fullProvider?.programDiscountRules?.[programKey];
    const cardRegion =
      match.region ||
      UtilityPartnerManager.regionFromProvider(fullProvider) ||
      "";

    return /** @type {UtilityPartner} */ ({
      code: match.code,
      handle: match.handle || fullProvider?.handle || "",
      programHandle: programKey,
      programType,
      mysaOfferMode,
      primaryIncentive,
      name: programName,
      providerName,
      region: cardRegion,
      logoSvg: match.logoSvg || fullProvider?.logoSvg || "",
      logoImg: match.logoImg || fullProvider?.logoImg || "",
      popupCopy: family?.popupCopy || fullProvider?.popupCopy || "",
      widgetDescription:
        family?.widgetDescription || fullProvider?.widgetDescription || "",
      active: true,
      hasSso: fullProvider?.hasSso === true,
      rebateAmount: Number(primaryIncentive?.amount || 0),
      programs: this.buildProgramsObjectForType(
        programType,
        family,
        apiProgram.handle,
      ),
      discountRules:
        programDiscounts?.discountRules ?? fullProvider?.discountRules,
      discountPrograms:
        programDiscounts?.discountPrograms ?? fullProvider?.discountPrograms,
    });
  }

  /**
   * Build a single-program nested object (page URL, incentive, popup copy).
   * @param {string} programType
   * @param {{ handle: string, name?: string, page_url?: string, primaryIncentive?: import('./utility-partner-manager').PrimaryIncentive, mysaOfferMode?: string } | null | undefined} family
   * @param {string} fallbackHandle
   * @returns {UtilityPartner['programs']}
   */
  buildProgramsObjectForType(programType, family, fallbackHandle) {
    const handle = family?.handle || fallbackHandle;
    const pageUrl = family?.page_url || programCollectionPath(handle);
    const primary = family?.primaryIncentive;

    if (programType === "rebate") {
      return {
        rebate: {
          program_handle: handle,
          page_url: primary?.marketplaceUrl || pageUrl,
          amount_pre_tax: primary?.amount ?? 0,
          amount_post_tax: primary?.amount ?? 0,
          mysaOfferMode: family?.mysaOfferMode || "",
          eligible_products_v2: primary?.eligibleProducts || [],
        },
      };
    }

    if (
      programType === "demand-response" ||
      programType === "demand_response"
    ) {
      return {
        demandResponse: {
          handle,
          program_name: family?.name || this.formatProgramHandle(handle),
          page_url: pageUrl,
        },
      };
    }

    return {};
  }

  /**
   * Humanize a program handle for display when metaobject data is missing.
   * @param {string} handle
   * @returns {string}
   */
  formatProgramHandle(handle) {
    return handle
      .replace(/-/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  /**
   * Render program cards and mark one as selected.
   * @param {UtilityPartner[]} cards
   * @param {UtilityPartner} selectedCard
   */
  renderProgramCardsWithSelection(cards, selectedCard) {
    this.renderProviders(cards);

    const selectedHandle =
      selectedCard.programHandle || selectedCard.code || "";
    let card = selectedHandle
      ? this.refs.providersList?.querySelector(
          `[data-program-handle="${CSS.escape(selectedHandle)}"]`,
        )
      : null;

    if (!card && selectedCard.code) {
      card = this.refs.providersList
        ?.querySelector(
          `.utility-wizard__provider-card input[data-provider-code="${CSS.escape(selectedCard.code)}"]`,
        )
        ?.closest(".utility-wizard__provider-card");
    }

    if (!card) return;

    card.classList.add("is-selected");
    const radio = /** @type {HTMLInputElement|null} */ (
      card.querySelector('input[type="radio"]')
    );
    if (radio) radio.checked = true;

    this.#setHasSelection(true);
    this.state.selectedProvider = selectedCard;
    show(this.refs.clearUtilityBtn);
    this.hideAllFooterButtons();
    show(this.refs.matchesButtons);
    this.syncSelectedCta(selectedCard);
    this.displayProgramInfo(selectedCard);
    show(this.refs.results);
    show(this.refs.utilityContent);
  }

  /**
   * Apply the offer-mode border class and fill `[data-offer]` with the program
   * name (second line under the utility name). Hidden when there is no program.
   * @param {HTMLElement | null | undefined} card
   * @param {UtilityPartner} provider
   */
  #applyProgramCardVariant(card, provider) {
    if (!card) return;

    for (const modifier of Object.values(OFFER_MODE_CARD_MODIFIERS)) {
      card.classList.remove(modifier);
    }

    const offerBadge = /** @type {HTMLElement | null} */ (
      card.querySelector("[data-offer]")
    );

    if (provider.mysaOfferMode) {
      const modifier = OFFER_MODE_CARD_MODIFIERS[provider.mysaOfferMode];
      if (modifier) card.classList.add(modifier);
    } else if (provider.programType === "demand-response") {
      card.classList.add(OFFER_MODE_CARD_MODIFIERS.demand_response);
    }

    const utilityName = provider.providerName || "";
    const programName =
      provider.programHandle && provider.name && provider.name !== utilityName
        ? provider.name
        : "";

    if (offerBadge) {
      offerBadge.classList.remove("utility-wizard__provider-offer--program");
      if (programName) {
        offerBadge.textContent = programName;
        offerBadge.classList.add("utility-wizard__provider-offer--program");
        show(offerBadge);
      } else {
        offerBadge.textContent = "";
        hide(offerBadge);
      }
    }
  }

  /**
   * Render provider cards
   * @param {UtilityPartner[]} providers
   */
  renderProviders(providers) {
    const container = this.refs.providersList;
    const template = this.refs.providerTemplate;

    if (!container || !template) return;

    container.innerHTML = "";

    providers.forEach((provider) => {
      const clone = /** @type {DocumentFragment} */ (
        template.content.cloneNode(true)
      );
      const card = /** @type {HTMLElement|null} */ (
        clone.querySelector(".utility-wizard__provider-card")
      );
      const radio = /** @type {HTMLInputElement|null} */ (
        clone.querySelector('input[type="radio"]')
      );
      const logo = clone.querySelector("[data-logo]");
      const name = clone.querySelector("[data-name]");
      const copy = clone.querySelector("[data-copy]");

      if (radio) {
        radio.value = provider.programHandle || provider.code;
        radio.dataset.providerCode = provider.code;
        radio.dataset.programHandle =
          provider.programHandle || provider.code || "";
        if (provider.mysaOfferMode) {
          radio.dataset.offerMode = provider.mysaOfferMode;
        }
      }

      if (logo) {
        // Prefer logoImg if available, fall back to logoSvg
        if (provider.logoImg) {
          const img = document.createElement("img");
          img.src = provider.logoImg;
          img.alt = provider.providerName || provider.name;
          img.loading = "lazy";
          logo.innerHTML = "";
          logo.appendChild(img);
        } else if (provider.logoSvg) {
          logo.innerHTML = "";
          const parsed = new DOMParser().parseFromString(
            provider.logoSvg,
            "image/svg+xml",
          );
          const svg = parsed.documentElement;
          if (svg?.nodeName.toLowerCase() === "svg") {
            sanitizeSvg(svg);
            svg.setAttribute("aria-hidden", "true");
            logo.appendChild(document.importNode(svg, true));
          } else {
            hide(logo);
          }
        } else {
          hide(logo);
        }
      }

      if (name) {
        name.textContent = provider.programHandle
          ? provider.providerName || provider.name
          : provider.name;
      }
      if (copy) {
        copy.textContent = provider.programHandle
          ? ""
          : provider.widgetDescription || provider.popupCopy || "";
      }

      if (card) {
        card.dataset.programHandle =
          provider.programHandle || provider.code || "";
        if (provider.mysaOfferMode) {
          card.dataset.offerMode = provider.mysaOfferMode;
        }
      }

      this.#applyProgramCardVariant(card, provider);

      card?.addEventListener("click", () => {
        this.handleProviderSelection(provider, card);
      });

      container.appendChild(clone);
    });

    this.#updateProvidersListScrollLimit();
  }

  /**
   * Limit the providers list to ~3.5 visible cards when there are more than
   * three programs, so a clipped fourth card signals scrollability.
   */
  #updateProvidersListScrollLimit() {
    const container = this.refs.providersList;
    if (!container || container.classList.contains("has-selection")) {
      container?.style.removeProperty("max-height");
      return;
    }

    const measure = () => {
      const cards = container.querySelectorAll(
        ".utility-wizard__provider-card",
      );
      if (cards.length <= 3) {
        container.style.removeProperty("max-height");
        return;
      }

      const firstCard = /** @type {HTMLElement} */ (cards[0]);
      const cardHeight = firstCard.offsetHeight;
      if (!cardHeight) return;

      const gap =
        Number.parseFloat(
          getComputedStyle(container).rowGap || getComputedStyle(container).gap,
        ) || 0;

      container.style.maxHeight = `${cardHeight * 3.5 + gap * 3}px`;
    };

    requestAnimationFrame(measure);
  }

  /**
   * Stable identity for a wizard card. Program cards share a provider code.
   * @param {UtilityPartner} provider
   * @returns {string}
   */
  #providerSelectionKey(provider) {
    return `${provider.code}|${provider.programHandle || ""}`;
  }

  /**
   * Handle provider selection.
   *
   * The card is a label around a radio. A pointer click fires the label
   * listener, then the browser synthesizes a click on the radio that bubbles
   * back to the same listener. Keyboard activation (Space / arrows) bubbles
   * once. The in-flight guard collapses that pair into one cart write.
   * A different partner clicked while a write is in flight replaces the
   * queue, so the latest click wins. A repeat of that latest partner is
   * ignored. The queued partner is posted only when it differs from the
   * write already running.
   *
   * @param {UtilityPartner} provider
   * @param {HTMLElement} card
   */
  async handleProviderSelection(provider, card) {
    const selectionKey = this.#providerSelectionKey(provider);

    if (this.#selectionInFlight) {
      const latestKey = this.#queuedSelection
        ? this.#providerSelectionKey(this.#queuedSelection.provider)
        : this.#selectionKey;

      if (latestKey === selectionKey) {
        log("Ignoring duplicate selection while a write is in flight");
        return;
      }

      this.#queuedSelection = { provider, card };
      log("Queued a different partner until the in-flight write finishes");
      return;
    }

    this.#selectionKey = selectionKey;
    this.#selectionInFlight = true;

    // Capture before overwriting state so restored / re-clicked current selection
    // does not re-fire provider_selected, while a new choice after postal re-search does.
    const alreadySelected =
      !!this.state.selectedProvider &&
      this.#providerSelectionKey(this.state.selectedProvider) === selectionKey;

    const allCards = this.refs.providersList?.querySelectorAll(
      ".utility-wizard__provider-card",
    );
    allCards?.forEach((c) => c.classList.remove("is-selected"));
    card.classList.add("is-selected");

    const radio = /** @type {HTMLInputElement|null} */ (
      card.querySelector('input[type="radio"]')
    );
    if (radio) radio.checked = true;

    this.state.selectedProvider = provider;

    // Show loading state on the selected card
    card.classList.add("is-loading");
    this.#setHasSelection(true);
    this.#updateProvidersListScrollLimit();

    // Hide all footers while saving
    this.hideAllFooterButtons();

    /** @type {{ provider: UtilityPartner, card: HTMLElement } | null} */
    let queued = null;

    /** @type {boolean} */
    let saved = false;

    try {
      saved = await this.saveProviderSelection(provider);
    } finally {
      card.classList.remove("is-loading");
      this.#selectionInFlight = false;
      queued = this.#queuedSelection;
      this.#queuedSelection = null;
    }

    // Paint and emit for the partner that remains. A queued partner that
    // matches this write was re-selected while it was saving, so posting
    // it again would duplicate the cart write.
    if (
      queued &&
      this.#providerSelectionKey(queued.provider) !== this.#selectionKey
    ) {
      await this.handleProviderSelection(queued.provider, queued.card);
      return;
    }

    if (saved && !alreadySelected) {
      this.#trackProviderSelected(provider);
    }

    show(this.refs.clearUtilityBtn);

    this.emit("utility-wizard:provider-selected", { provider });

    show(this.refs.matchesButtons);
    this.syncSelectedCta(provider);
    this.displayProgramInfo(provider);
    this.#revealSelectedCta();
  }

  /**
   * On mobile the sheet is a short scroller. Selecting a program adds the
   * offer copy and pushes the CTA below the fold, so scroll the sheet to it.
   */
  #revealSelectedCta() {
    if (!window.matchMedia("(max-width: 479px)").matches) return;

    const sheet = this.closest(".location-input-wrapper");
    const footer = this.refs.matchesButtons;
    if (!(sheet instanceof HTMLElement) || !footer) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    requestAnimationFrame(() => {
      const sheetRect = sheet.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      const padding =
        Number.parseFloat(getComputedStyle(sheet).paddingBottom) || 0;
      const overflow = footerRect.bottom + padding - sheetRect.bottom;
      if (overflow <= 1) return;

      sheet.scrollTo({
        top: sheet.scrollTop + overflow,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    });
  }

  /**
   * Display utility_popup_copy from provider metaobject
   * @param {UtilityPartner} provider
   */
  displayProgramInfo(provider) {
    const programInfo = this.refs.programInfo;
    const programCopy = this.refs.programCopy;

    log("displayProgramInfo", {
      hasProgramInfo: !!programInfo,
      hasProgramCopy: !!programCopy,
      hasPopupCopy: !!provider.popupCopy,
      popupCopyLength: provider.popupCopy?.length,
    });

    if (!programInfo || !programCopy) return;

    if (provider.popupCopy) {
      programCopy.textContent = provider.popupCopy;
      show(programInfo);
      log("displayProgramInfo: shown");
    } else {
      hide(programInfo);
      log("displayProgramInfo: hidden (no popupCopy)");
    }
  }

  /**
   * Page URL for the selected program. `buildProgramsObjectForType` already
   * resolved marketplace / collection URLs into `page_url`; the handle fallback
   * only covers the stub provider built before family data is available.
   * That fallback is prefixed with the current locale root.
   * @param {UtilityPartner} provider
   * @returns {string}
   */
  #programPageUrl(provider) {
    const programs =
      /** @type {{rebate?: {page_url?: string}, demandResponse?: {page_url?: string}} | undefined} */ (
        provider.programs
      );
    const pageUrl =
      programs?.rebate?.page_url || programs?.demandResponse?.page_url;
    if (pageUrl) return pageUrl;
    return programCollectionPath(provider.programHandle);
  }

  /**
   * Pick the single CTA for a selected card: View offer when the program has
   * a page, otherwise Continue shopping (which only closes the popup).
   * @param {UtilityPartner} provider
   */
  syncSelectedCta(provider) {
    const closeBtn = this.refs.matchesContinueButton;
    const offerLink = this.refs.matchesOfferLink;
    const offerWrap = this.refs.matchesOfferLinkWrap;
    const href = provider ? safeUrl(this.#programPageUrl(provider)) : "";

    if (href && href !== "#" && offerLink) {
      offerLink.href = href;
      show(offerWrap);
      hide(closeBtn);
      return;
    }

    if (offerLink) offerLink.href = "#";
    hide(offerWrap);
    show(closeBtn);
  }

  /**
   * Save provider selection to utility manager
   * @param {UtilityPartner} provider
   * @returns {Promise<boolean>}
   */
  async saveProviderSelection(provider) {
    try {
      const saved = await this.utilityManager?.setPartner(
        {
          code: provider.code,
          name: provider.providerName || provider.name,
          region:
            provider.region ||
            UtilityPartnerManager.regionFromProvider(provider) ||
            "",
          handle: provider.handle,
          logoUrl: provider.logoUrl,
          programHandle: provider.programHandle,
          mysaOfferMode: provider.mysaOfferMode,
        },
        this.state.postalCode,
      );

      if (saved === true) {
        // Market-specific pricing/content: switch Shopify locale when the
        // selected utility belongs to a different country than the current one.
        this.#switchLocaleForUtility(provider);
      }
      return saved === true;
    } catch (error) {
      console.error("[UtilitySelectorWizard] Error saving provider:", error);
      reportError(error);
      return false;
    }
  }

  /**
   * Resolve the country a utility belongs to.
   * Prefer the postal/ZIP used for the lookup, then the provider code prefix.
   * @param {UtilityPartner | { code?: string }} [provider]
   * @returns {string}
   */
  #resolveUtilityCountry(provider) {
    const catalogProvider =
      provider?.code || provider?.handle
        ? this.utilityManager?.getProviderByCode(
            provider.code || provider.handle || "",
          )
        : null;

    return (
      this.state.country ||
      getCountryFromPostalCode(this.state.postalCode) ||
      UtilityStorage.getPostalCode()?.country ||
      provider?.country?.code ||
      catalogProvider?.country?.code ||
      getCountryFromProviderCode(provider?.code || provider?.handle || "") ||
      ""
    );
  }

  /**
   * If the selected utility is outside the current Shopify country/locale,
   * submit the localization form so the storefront switches markets.
   * @param {UtilityPartner | { code?: string }} [provider]
   */
  #switchLocaleForUtility(provider) {
    const utilityCountry = this.#resolveUtilityCountry(provider);
    const currentCountry = (
      this.dataset.country ||
      this.country ||
      ""
    ).toUpperCase();

    if (!utilityCountry || !currentCountry) return;
    if (utilityCountry.toUpperCase() === currentCountry) return;

    log(
      `Utility country ${utilityCountry} differs from locale ${currentCountry} — switching after selection`,
    );
    this.selectCountry(utilityCountry);
  }

  /**
   * Derive GA4 wizard_context from the parent container surface.
   * Top-nav hosts the wizard in <details>; on ≤479px that surface is a
   * fixed bottom sheet (see top-nav-area-code / postal-code-input), so
   * report it as "modal". Real <dialog> hosts (mysa-modal) also map to
   * "modal". Desktop top-nav details stay "top-nav-dropdown".
   * @returns {"top-nav-dropdown" | "modal" | "homepage"}
   */
  #getWizardContext() {
    if (this.closest("dialog")) return "modal";
    if (this.closest("details")) {
      // Same breakpoint as postal-code-input mobile bottom sheet
      if (window.matchMedia("(max-width: 479px)").matches) return "modal";
      return "top-nav-dropdown";
    }
    return "homepage";
  }

  /**
   * Fire a utility-wizard GA4 event with shared engagement params.
   * @param {string} name
   * @param {Record<string, unknown>} [params]
   */
  #emitWizardEvent(name, params = {}) {
    sendGaEvent(name, {
      wizard_context: this.#getWizardContext(),
      value: 1,
      currency: getActiveCurrency(),
      ...params,
    });
  }

  /**
   * Fire utility_wizard_opened when the parent container opens.
   * Dedupes against the parent container so reconnect while still open
   * (or bind-time sync after an early open) does not double-count.
   */
  #trackOpened() {
    const container = this.closest("details") || this.closest("dialog");
    if (container?.dataset.utilityWizardOpened === "true") return;
    if (container) container.dataset.utilityWizardOpened = "true";

    this.#emitWizardEvent("utility_wizard_opened", {
      page_path: window.location.pathname,
    });
  }

  /**
   * Clear the per-open opened-event guard so the next open can fire again.
   */
  #clearOpenedTracked() {
    const container = this.closest("details") || this.closest("dialog");
    if (container) delete container.dataset.utilityWizardOpened;
  }

  /**
   * Province/region for analytics from the store catalog row (metaobject
   * `province.code` / stamped `region`). Requires provider JSON keyed the
   * same way as the postal API handle — see `utility-provider-data.liquid`
   * (v2 catalog when present, else legacy app-scoped).
   * @param {{ region?: string, province?: string | { code?: string, iso?: string } } | null | undefined} provider
   * @returns {string}
   */
  #regionForAnalytics(provider) {
    return UtilityPartnerManager.regionFromProvider(provider) || "unknown";
  }

  /**
   * Fire utility_wizard_postal_submitted after a successful postal lookup.
   * Caller must gate on success — failed checks must not fire.
   * @param {UtilityPartner[]} providers
   */
  #trackPostalSubmitted(providers) {
    this.#emitWizardEvent("utility_wizard_postal_submitted", {
      country: this.state.country || "unknown",
      province: this.#regionForAnalytics(providers[0]),
      has_results: providers.length > 0,
      results_count: providers.length,
    });
  }

  /**
   * Fire utility_wizard_provider_selected after a successful interactive save
   * of a provider that was not already selected in this wizard instance.
   * @param {UtilityPartner} provider
   */
  #trackProviderSelected(provider) {
    this.#emitWizardEvent("utility_wizard_provider_selected", {
      provider_code: provider.code || "unknown",
      provider_name: provider.providerName || provider.name || "unknown",
      region: this.#regionForAnalytics(provider),
      is_sso: provider.hasSso === true,
    });
  }

  /**
   * Fire utility_wizard_cleared after an interactive clear of a selected provider.
   * Caller must gate on hadSelection — restart-without-selection must not fire.
   */
  #trackCleared() {
    this.#emitWizardEvent("utility_wizard_cleared");
  }
}

// Register custom element
if (!customElements.get("utility-selector-wizard")) {
  customElements.define("utility-selector-wizard", UtilitySelectorWizard);
}

export { UtilitySelectorWizard };
